/**
 * Modal para subir pagos de un club a un evento.
 * El director sube comprobantes de pago a nivel de club (no por persona).
 * Los pagos son validados después por la asociación.
 */

import { useState, useRef } from "react";
import { Button, Badge } from "@/components/ui";
import { toast } from "react-hot-toast";
import {
  X,
  Upload,
  FileImage,
  FileText,
  Loader2,
  CheckCircle,
  AlertCircle,
  DollarSign,
  Trash2,
  Clock,
  XCircle,
} from "lucide-react";
import { useClubEventPayments, useUploadPayment } from "@/hooks/useEvents";
import { parseAmountCOP, formatCOP } from "@/lib/money";
import type { Event, EventPaymentStatus } from "@/types";

interface PaymentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: Event;
  clubId: string;
  clubName: string;
  /** Monto total esperado según las inscripciones activas del club. */
  expectedAmount?: number;
  /** Número de inscripciones activas (para el desglose). */
  registrationsCount?: number;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
];

const statusLabels: Record<EventPaymentStatus, string> = {
  pending: "Pendiente",
  approved: "Aprobado",
  rejected: "Rechazado",
};

const statusColors: Record<EventPaymentStatus, string> = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};

const toSafeDate = (timestamp: unknown): Date => {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  if (
    typeof timestamp === "object" &&
    "toDate" in timestamp &&
    typeof (timestamp as { toDate: () => Date }).toDate === "function"
  ) {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  if (typeof timestamp === "object" && "seconds" in timestamp) {
    return new Date((timestamp as { seconds: number }).seconds * 1000);
  }
  return new Date();
};

export default function PaymentUploadModal({
  isOpen,
  onClose,
  event,
  clubId,
  clubName,
  expectedAmount = 0,
  registrationsCount = 0,
}: PaymentUploadModalProps) {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: payments, isLoading } = useClubEventPayments(clubId, event.id);
  const uploadPayment = useUploadPayment();

  const approvedAmount =
    payments
      ?.filter((p) => p.status === "approved")
      .reduce((sum, p) => sum + p.amount, 0) ?? 0;
  const pendingAmount =
    payments
      ?.filter((p) => p.status === "pending")
      .reduce((sum, p) => sum + p.amount, 0) ?? 0;
  const remainingAmount = Math.max(
    expectedAmount - approvedAmount - pendingAmount,
    0,
  );
  const overpaid =
    expectedAmount > 0 && approvedAmount + pendingAmount > expectedAmount;
  const fmt = (n: number) => `$${n.toLocaleString("es-CO")}`;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (!ALLOWED_TYPES.includes(selected.type)) {
      alert("Solo se permiten archivos JPG, PNG, WebP o PDF.");
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      alert("El archivo no puede superar 10MB.");
      return;
    }
    // Materializar en memoria: en móvil el File es solo una referencia que el
    // SO puede invalidar antes del submit (NotReadableError al subir).
    try {
      const buffer = await selected.arrayBuffer();
      setFile(
        new File([buffer], selected.name, {
          type: selected.type,
          lastModified: selected.lastModified,
        }),
      );
    } catch (err) {
      console.error("No se pudo leer el archivo seleccionado:", err);
      alert("No se pudo leer el archivo. Vuelve a seleccionarlo.");
    }
  };

  const handleUpload = async () => {
    console.log("[PaymentModal] handleUpload called", {
      file: file?.name,
      fileSize: file?.size,
      fileType: file?.type,
      amount,
      eventId: event.id,
      clubId,
    });
    if (!file || !amount) {
      console.warn("[PaymentModal] Missing file or amount", {
        hasFile: !!file,
        amount,
      });
      return;
    }

    // parseFloat leía "1.350.000" como 1.35 (el punto de miles colombiano):
    // ver src/lib/money.ts.
    const numericAmount = parseAmountCOP(amount);
    if (numericAmount === null || numericAmount <= 0) {
      toast.error("Ingresa un monto válido");
      return;
    }

    console.log("[PaymentModal] Starting mutateAsync", {
      numericAmount,
      fileName: file.name,
      fileSize: file.size,
    });
    setUploading(true);
    try {
      await uploadPayment.mutateAsync({
        eventId: event.id,
        eventName: event.name,
        clubId,
        clubName,
        amount: numericAmount,
        file,
        description: description || undefined,
      });
      console.log("[PaymentModal] mutateAsync SUCCESS");

      // Limpiar formulario
      setAmount("");
      setDescription("");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";

      // Si era el primer pago del club, cerrar modal automáticamente
      // (con un pago + ≥10 inscritos el club ya entra al ranking).
      const wasFirstPayment = (payments?.length ?? 0) === 0;
      if (wasFirstPayment) {
        toast.success(
          "¡Pago registrado! Tu club ahora puede entrar al ranking.",
        );
        onClose();
      }
    } catch (err) {
      console.error("[PaymentModal] mutateAsync FAILED", err);
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b bg-gradient-to-r from-emerald-600 to-teal-600 rounded-t-2xl shrink-0">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 bg-white/20 rounded-xl flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-xl font-semibold text-white truncate">
                Subir Pago
              </h2>
              <p className="text-xs sm:text-sm text-emerald-100 mt-0.5 sm:mt-1 truncate">
                {event.name} • {clubName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors shrink-0"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0 space-y-4 sm:space-y-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : (
            <>
              {/* Resumen de pagos */}
              {expectedAmount > 0 && (
                <div className="p-3 sm:p-4 rounded-2xl border-2 border-emerald-100 bg-gradient-to-br from-emerald-50 to-teal-50 space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="text-[10px] sm:text-xs uppercase tracking-wide text-emerald-700 font-semibold">
                        Total a pagar
                      </p>
                      <p className="text-2xl sm:text-3xl font-bold text-emerald-900">
                        {fmt(expectedAmount)}
                      </p>
                      {registrationsCount > 0 && (
                        <p className="text-xs text-emerald-700 mt-0.5">
                          {registrationsCount}{" "}
                          {registrationsCount === 1
                            ? "inscrito activo"
                            : "inscritos activos"}
                        </p>
                      )}
                    </div>
                    <div className="sm:text-right">
                      <p className="text-[10px] sm:text-xs uppercase tracking-wide text-gray-600 font-semibold">
                        {overpaid ? "Excedente" : "Saldo pendiente"}
                      </p>
                      <p
                        className={`text-2xl sm:text-3xl font-bold ${
                          remainingAmount === 0
                            ? "text-green-700"
                            : overpaid
                              ? "text-blue-700"
                              : "text-amber-700"
                        }`}
                      >
                        {fmt(
                          overpaid
                            ? approvedAmount + pendingAmount - expectedAmount
                            : remainingAmount,
                        )}
                      </p>
                      {remainingAmount === 0 && !overpaid && (
                        <p className="text-xs text-green-700 mt-0.5 font-medium">
                          ✓ Cubierto
                        </p>
                      )}
                    </div>
                  </div>
                  {/* Barra de progreso */}
                  <div className="space-y-1">
                    <div className="h-2 bg-white/60 rounded-full overflow-hidden flex">
                      <div
                        className="bg-green-500 h-full transition-all"
                        style={{
                          width: `${Math.min((approvedAmount / expectedAmount) * 100, 100)}%`,
                        }}
                      />
                      <div
                        className="bg-blue-400 h-full transition-all"
                        style={{
                          width: `${Math.min((pendingAmount / expectedAmount) * 100, 100 - Math.min((approvedAmount / expectedAmount) * 100, 100))}%`,
                        }}
                      />
                    </div>
                    <div className="flex justify-between text-[10px] text-gray-600">
                      <span>
                        {Math.round(
                          ((approvedAmount + pendingAmount) / expectedAmount) *
                            100,
                        )}
                        % registrado
                      </span>
                      <span>
                        {Math.round((approvedAmount / expectedAmount) * 100)}%
                        aprobado
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                <div className="p-2 sm:p-3 bg-green-50 rounded-xl text-center">
                  <p className="text-lg sm:text-2xl font-bold text-green-700">
                    ${approvedAmount.toLocaleString("es-CO")}
                  </p>
                  <p className="text-[10px] sm:text-xs text-green-600">
                    Aprobado
                  </p>
                </div>
                <div className="p-2 sm:p-3 bg-blue-50 rounded-xl text-center">
                  <p className="text-lg sm:text-2xl font-bold text-blue-700">
                    ${pendingAmount.toLocaleString("es-CO")}
                  </p>
                  <p className="text-[10px] sm:text-xs text-blue-600">
                    En revisión
                  </p>
                </div>
                <div className="p-2 sm:p-3 bg-emerald-50 rounded-xl text-center">
                  <p className="text-lg sm:text-2xl font-bold text-emerald-700">
                    {payments?.length ?? 0}
                  </p>
                  <p className="text-[10px] sm:text-xs text-emerald-600">
                    Pagos subidos
                  </p>
                </div>
              </div>

              {/* Historial de pagos */}
              {payments && payments.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">
                    Pagos anteriores
                  </h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {payments.map((payment) => (
                      <div
                        key={payment.id}
                        className="flex items-center gap-3 p-3 rounded-xl border-2 border-gray-100"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900">
                              ${payment.amount.toLocaleString("es-CO")}
                            </p>
                            <Badge
                              className={`${statusColors[payment.status]} text-xs`}
                            >
                              {payment.status === "pending" && (
                                <Clock className="w-3 h-3 mr-1" />
                              )}
                              {payment.status === "approved" && (
                                <CheckCircle className="w-3 h-3 mr-1" />
                              )}
                              {payment.status === "rejected" && (
                                <XCircle className="w-3 h-3 mr-1" />
                              )}
                              {statusLabels[payment.status]}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-500">
                            {payment.description || "Sin descripción"} •{" "}
                            {toSafeDate(payment.uploadedAt).toLocaleDateString(
                              "es-CO",
                              {
                                day: "numeric",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                          </p>
                          {payment.rejectionReason && (
                            <p className="text-xs text-red-600 mt-1">
                              Motivo: {payment.rejectionReason}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Formulario de nuevo pago */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Nuevo pago</h3>

                {/* Monto */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Monto del pago *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                      $
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={amount}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^\d.,]/g, "");
                        setAmount(val);
                      }}
                      placeholder="100.000"
                      className="w-full pl-8 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 text-lg font-medium transition-all duration-300"
                    />
                  </div>
                  {/* Confirmación de lo que se va a guardar: el monto escrito a
                      mano es la causa histórica de los pagos mal registrados. */}
                  {amount.trim() !== "" &&
                    (parseAmountCOP(amount) === null ||
                    parseAmountCOP(amount) === 0 ? (
                      <p className="mt-1.5 text-sm text-red-600">
                        No se entiende ese monto. Escríbelo así: 1.350.000
                      </p>
                    ) : (
                      <p className="mt-1.5 text-sm text-gray-600">
                        Vas a registrar{" "}
                        <strong className="text-gray-900">
                          {formatCOP(parseAmountCOP(amount) as number)}
                        </strong>
                      </p>
                    ))}
                </div>

                {/* Descripción */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción (opcional)
                  </label>
                  <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Ej: Pago parcial inscripciones campamento"
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm transition-all duration-300"
                  />
                </div>

                {/* Comprobante */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Comprobante de pago *
                  </label>
                  {file ? (
                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
                      {file.type === "application/pdf" ? (
                        <FileText className="w-8 h-8 text-red-500 flex-shrink-0" />
                      ) : (
                        <FileImage className="w-8 h-8 text-blue-500 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(file.size / 1024).toFixed(0)} KB
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setFile(null);
                          if (fileInputRef.current)
                            fileInputRef.current.value = "";
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center gap-3 p-8 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 transition-all duration-200">
                      <Upload className="w-10 h-10 text-gray-400" />
                      <div className="text-center">
                        <p className="font-medium text-gray-700">
                          Arrastra o haz clic para seleccionar
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          JPG, PNG, WebP o PDF (máx. 10MB)
                        </p>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp,.pdf"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Nota informativa */}
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl text-sm">
                <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-blue-700">
                  <p className="font-medium">Información importante</p>
                  <p className="mt-1">
                    Puedes subir múltiples pagos para tu club. Cada pago será
                    revisado por la asociación. Para el ranking se requiere un
                    mínimo de <strong>10 inscritos</strong> y al menos{" "}
                    <strong>1 pago subido</strong>.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 sm:p-6 border-t bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shrink-0">
          <div className="text-xs sm:text-sm text-gray-600">
            {uploading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Subiendo pago...
              </span>
            ) : (
              <span>
                {amount && file ? "Listo para subir" : "Completa el formulario"}
              </span>
            )}
          </div>
          <div className="flex gap-2 sm:gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={uploading}
              className="flex-1 sm:flex-none"
            >
              Cerrar
            </Button>
            <Button
              type="button"
              onClick={() => {
                console.log("[PaymentModal] BUTTON CLICKED", {
                  uploading,
                  hasFile: !!file,
                  amount,
                });
                handleUpload();
              }}
              disabled={uploading || !file || !amount}
              className="bg-emerald-600 hover:bg-emerald-700 flex-1 sm:flex-none"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Subir Pago
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
